import { TAB_STRINGS, TAB_FRETS } from '../../config/songbook'
import { playChord } from '../../lib/chordPlayer'
import type { TabDot } from '../../types/songbook'

interface GuitarTabProps {
  dots: TabDot[]
  startFret?: number
  onChange?: (dots: TabDot[]) => void
  onStartFretChange?: (fret: number) => void
  size?: 'sm' | 'md'
}

const STRING_LABELS = ['e', 'B', 'G', 'D', 'A', 'E']

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII', 'XIV', 'XV']

export function GuitarTab({ dots, startFret = 1, onChange, onStartFretChange, size = 'md' }: GuitarTabProps) {
  const isInteractive = !!onChange

  // Dimensions
  const labelAreaW = size === 'sm' ? 28 : 35
  const w = size === 'sm' ? 120 : 160
  const svgH = size === 'sm' ? 150 : 200
  const nutX = labelAreaW + 5
  const rightX = w - 10
  const topY = size === 'sm' ? 25 : 35
  const bottomY = svgH - (size === 'sm' ? 15 : 20)
  const stringSpacing = (bottomY - topY) / (TAB_STRINGS - 1)
  const fretSpacing = (rightX - nutX) / TAB_FRETS

  const getStringY = (s: number) => topY + s * stringSpacing
  const getFretX = (f: number) => nutX + (f + 0.5) * fretSpacing

  const hasDot = (s: number, f: number) =>
    dots.some((d) => d.string === s && d.fret === f)

  const isOpenString = (s: number) =>
    dots.some((d) => d.string === s && d.fret === -1)

  const handleTap = (s: number, f: number) => {
    if (!onChange) return

    if (hasDot(s, f)) {
      onChange(dots.filter((d) => !(d.string === s && d.fret === f)))
    } else {
      // One dot per string: remove existing dot on this string (including open string)
      const filtered = dots.filter((d) => d.string !== s)
      onChange([...filtered, { string: s, fret: f }])
    }
  }

  const handleOpenStringTap = (s: number) => {
    if (!onChange) return

    if (isOpenString(s)) {
      // Remove open string marker
      onChange(dots.filter((d) => !(d.string === s && d.fret === -1)))
    } else {
      // Set open string: remove any fretted dot on this string, add open
      const filtered = dots.filter((d) => d.string !== s)
      onChange([...filtered, { string: s, fret: -1 }])
    }
  }

  const dotRadius = size === 'sm' ? 5 : 7
  const tapRadius = 12
  const isFirstFret = startFret <= 1
  const fretFontSize = size === 'sm' ? 7 : 9
  const labelFontSize = size === 'sm' ? 8 : 10

  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${svgH}`}
        width={w}
        height={svgH}
        className="select-none"
      >
        {/* Nut (thick only if starting at fret 1) */}
        <line
          x1={nutX} y1={topY - 4}
          x2={nutX} y2={bottomY + 4}
          stroke={isFirstFret ? '#ccc' : '#555'}
          strokeWidth={isFirstFret ? 3 : 1}
        />

        {/* Fret lines */}
        {Array.from({ length: TAB_FRETS }, (_, f) => (
          <line
            key={`fret-${f}`}
            x1={nutX + (f + 1) * fretSpacing} y1={topY - 2}
            x2={nutX + (f + 1) * fretSpacing} y2={bottomY + 2}
            stroke="#555" strokeWidth={1}
          />
        ))}

        {/* Fret numbers below */}
        {Array.from({ length: TAB_FRETS }, (_, f) => {
          const fretNum = (startFret <= 1 ? 1 : startFret) + f
          const display = ROMAN[fretNum] ?? String(fretNum)
          return (
            <text
              key={`fretnum-${f}`}
              x={getFretX(f)}
              y={bottomY + (size === 'sm' ? 12 : 16)}
              fontSize={fretFontSize}
              fill="#666"
              textAnchor="middle"
            >
              {display}
            </text>
          )
        })}

        {/* Strings */}
        {Array.from({ length: TAB_STRINGS }, (_, s) => (
          <line
            key={`string-${s}`}
            x1={nutX} y1={getStringY(s)}
            x2={rightX} y2={getStringY(s)}
            stroke="#777" strokeWidth={1}
          />
        ))}

        {/* String labels + open string "O" */}
        {STRING_LABELS.map((label, s) => {
          const open = isOpenString(s)
          return (
            <g key={`label-${s}`}>
              {/* Open string "O" indicator */}
              {open && (
                <text
                  x={nutX - 7}
                  y={getStringY(s) + (size === 'sm' ? 4 : 5)}
                  fontSize={size === 'sm' ? 11 : 14}
                  fill="white"
                  fontWeight="bold"
                  textAnchor="middle"
                >
                  O
                </text>
              )}
              {/* String name */}
              <text
                x={open ? nutX - 18 : nutX - 12}
                y={getStringY(s) + (size === 'sm' ? 3 : 4)}
                fontSize={labelFontSize}
                fill="#888"
                textAnchor="middle"
              >
                {label}
              </text>
              {/* Tap target for open string toggle */}
              {isInteractive && (
                <rect
                  x={0}
                  y={getStringY(s) - tapRadius}
                  width={nutX - 2}
                  height={tapRadius * 2}
                  fill="transparent"
                  className="cursor-pointer"
                  onClick={() => handleOpenStringTap(s)}
                />
              )}
            </g>
          )
        })}

        {/* Fretted dots */}
        {dots
          .filter((d) => d.fret >= 0)
          .map((dot) => (
            <circle
              key={`dot-${dot.string}-${dot.fret}`}
              cx={getFretX(dot.fret)}
              cy={getStringY(dot.string)}
              r={dotRadius}
              fill="white"
            />
          ))}

        {/* Tap targets for frets (interactive mode only) */}
        {isInteractive &&
          Array.from({ length: TAB_STRINGS }, (_, s) =>
            Array.from({ length: TAB_FRETS }, (_, f) => (
              <circle
                key={`tap-${s}-${f}`}
                cx={getFretX(f)}
                cy={getStringY(s)}
                r={tapRadius}
                fill="transparent"
                className="cursor-pointer"
                onClick={() => handleTap(s, f)}
              />
            ))
          )}
      </svg>

      {/* Play button */}
      {dots.length > 0 && (
        <div
          className="mt-1 flex items-center justify-center"
          onClick={() => playChord(dots, startFret)}
        >
          <span className="flex items-center gap-1 rounded-full bg-primary/20 px-3 py-1.5 text-xs font-medium text-primary active:bg-primary active:text-white cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
            Play
          </span>
        </div>
      )}

      {isInteractive && onStartFretChange && (
        <div className="mt-0.5 flex items-center justify-center gap-1">
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-surface-lighter hover:text-text disabled:opacity-30"
            onClick={() => onStartFretChange(Math.max(1, startFret - 1))}
            disabled={startFret <= 1}
          >
            −
          </button>
          <span className="min-w-[40px] text-center text-xs text-text-muted">
            {ROMAN[startFret] ?? startFret}. Bund
          </span>
          <button
            className="rounded px-1.5 py-0.5 text-xs text-text-muted hover:bg-surface-lighter hover:text-text disabled:opacity-30"
            onClick={() => onStartFretChange(Math.min(15, startFret + 1))}
            disabled={startFret >= 15}
          >
            +
          </button>
        </div>
      )}
    </div>
  )
}
